import { api } from '../client'
import type { Session, User } from '../types'

interface MeResponse {
  user: User
}

type LoggedIn = MeResponse & { reactivated: boolean }

/** 开了两步验证的账号：密码对了只给 challenge，不发 cookie（D89） */
export interface TwoFactorChallenge {
  two_factor_required: true
  challenge: string
}

export type LoginResult = LoggedIn | TwoFactorChallenge

export function needsSecondFactor(result: LoginResult): result is TwoFactorChallenge {
  return 'two_factor_required' in result
}

export interface TwoFactorStatus {
  enabled: boolean
  enabled_at: string | null
  recovery_codes_left: number
}

export interface TwoFactorSetup {
  secret: string
  otpauth_url: string
  qr_svg: string
}

export const authApi = {
  me: () => api.get<MeResponse>('/auth/me').then((r) => r.user),

  /** 注册前的工作量证明题（D95） */
  powChallenge: () =>
    api.get<{ challenge: string; salt: string; difficulty: number; expires_in: number }>(
      '/auth/pow',
    ),

  register: (payload: {
    username: string
    email: string
    password: string
    pow: { challenge: string; nonce: string }
  }) => api.post<MeResponse>('/auth/register', payload).then((r) => r.user),

  /** reactivated=true 表示这次登录顺手恢复了一个停用期内的账号 */
  login: (payload: { identifier: string; password: string }) =>
    api.post<LoginResult>('/auth/login', payload),

  /** 第二步：验证器里的 6 位数字或一个恢复码 */
  loginTwoFactor: (payload: { challenge: string; code: string }) =>
    api.post<LoggedIn>('/auth/login/2fa', payload),

  logout: () => api.post<{ ok: boolean }>('/auth/logout'),

  refresh: () => api.post<MeResponse>('/auth/refresh').then((r) => r.user),

  // ---- 邮箱验证与密码 ----

  resendVerification: () =>
    api.post<{ sent: boolean; email_verified: boolean }>('/auth/verify-email/send'),

  verifyEmail: (token: string) =>
    api.post<{ ok: boolean; username: string }>('/auth/verify-email', { token }),

  /** 邮件里的 6 位验证码；要登录 */
  verifyEmailCode: (code: string) =>
    api.post<{ ok: boolean; email_verified: boolean }>('/auth/verify-email/code', { code }),

  /** 无论邮箱是否注册过都返回成功，前端也按同一种结果展示 */
  forgotPassword: (email: string) =>
    api.post<{ ok: boolean; message: string }>('/auth/password/forgot', { email }),

  resetPassword: (token: string, password: string) =>
    api.post<{ ok: boolean; username: string }>('/auth/password/reset', { token, password }),

  /** 撤销全部会话并给当前设备换一套新 token，所以调用后不会掉线 */
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: boolean; revoked_sessions: number }>('/auth/password/change', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  /** 要当前密码；新邮箱收验证信，旧邮箱收一封变更通知 */
  changeEmail: (email: string, password: string) =>
    api.post<MeResponse>('/auth/email/change', { email, password }).then((r) => r.user),

  /** 撤销全部会话并清 cookie；reactivation_days 天内重新登录即可恢复 */
  deactivate: (password: string) =>
    api.post<{ ok: boolean; reactivation_days: number }>('/auth/deactivate', { password }),

  // ---- 两步验证（D88–D90），都只走网页会话 ----

  twoFactorStatus: () => api.get<TwoFactorStatus>('/auth/2fa'),

  twoFactorSetup: (password: string) => api.post<TwoFactorSetup>('/auth/2fa/setup', { password }),

  /** 开启之后其它设备退出登录；恢复码只在这一次返回 */
  twoFactorEnable: (code: string) =>
    api.post<{ recovery_codes: string[]; revoked_sessions: number }>('/auth/2fa/enable', {
      code,
    }),

  twoFactorDisable: (password: string, code: string) =>
    api.post<TwoFactorStatus>('/auth/2fa/disable', { password, code }),

  regenerateRecoveryCodes: (password: string) =>
    api.post<{ recovery_codes: string[] }>('/auth/2fa/recovery-codes', { password }),

  // ---- 登录设备 ----

  sessions: () => api.get<{ items: Session[] }>('/auth/sessions').then((r) => r.items),

  revokeSession: (id: string) => api.delete<{ ok: boolean }>(`/auth/sessions/${id}`),
}
